/* ============================================================
   <melusio-overlay> — the Melusio overlay primitive (Cut 2).
   One reusable modal surface for the mobile menu + search now, and the
   cart + quick-view in Cut 3 (drop content in, zero new plumbing).

   Markup contract:
     <melusio-overlay id="ID" data-placement="left|right|top|center" data-label="…">
       <div data-overlay-scrim></div>
       <div data-overlay-panel> … <button data-overlay-close>× </button> … </div>
     </melusio-overlay>

   Triggers anywhere: <button data-overlay-open="ID" aria-controls="ID" aria-expanded="false">

   Owns: open/close, scrim, body scroll-lock with scrollbar-width compensation
   (no layout jump), focus trap + restore to trigger, Escape, background `inert`,
   role=dialog / aria-modal, ONE overlay open at a time, ~220ms entrance
   (none under prefers-reduced-motion).
   ============================================================ */
(function () {
  const OPEN = new Set();
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---- shared scroll lock (ref-counted) with scrollbar compensation ----
  let locks = 0;
  function lockScroll() {
    if (locks++ > 0) return;
    const sbw = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--melusio-sbw', sbw + 'px');
    document.body.classList.add('melusio-scroll-locked');
  }
  function unlockScroll() {
    if (locks > 0) locks--;
    if (locks > 0) return;
    document.body.classList.remove('melusio-scroll-locked');
    document.documentElement.style.removeProperty('--melusio-sbw');
  }

  // ---- inert every direct <body> child except the open overlay ----
  // Recomputed on each open because apps (chat widgets, pixels) inject direct
  // body children late — and so does the platform, which is why the exclusion
  // below is evaluated here rather than resolved once. The overlay is a direct body child
  // in Cut 3 (cart/quick-view/notify are statically rendered in theme.liquid);
  // when an overlay is nested deeper we walk up to its top-level ancestor so the
  // whole branch containing it stays live. script/style/link/template are never
  // interactive, so skipping them avoids pointless attribute churn.
  const SKIP_INERT = new Set(['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE']);

  /* Platform-owned surfaces that paint ABOVE every overlay and that we do not own.
     Shopify's consent banner is SECTION#shopify-pc__banner, a direct <body> child
     at z-index 2000000 — four orders of magnitude above an overlay's 200 — so it
     keeps covering the cart drawer's checkout button whether or not we inert it.
     Inerting it therefore removes the only control that makes it go away, and the
     customer can neither check out nor dismiss the thing blocking checkout without
     first closing the drawer. Excluding it is the whole fix: it stays where Shopify
     put it, at the size Shopify gave it, and stays clickable. #shopify-pc__prefs is
     the preferences dialog behind that banner's second button and has the same
     problem. The staff preview bar is not a customer surface but is inerted the same
     way and traps staff the same way, so it comes along.

     Matched by id, not by "anything injected late". The sweep is meant to inert app
     widgets — a chat bubble behind an open dialog is correct — so the exclusion is a
     named list, not a category. Shopify can restructure these without notice; the
     shopify-pc__ prefix is the part least likely to move, being their own namespace.
     If it does move, the failure is the bug we have today, not a worse one. */
  const PLATFORM_SURFACES = '[id^="shopify-pc__"], #PBarNextFrameWrapper, #preview-bar-iframe';

  function setInertBackground(node, on) {
    // the direct <body> child that contains this overlay (or the overlay itself)
    let branch = node;
    while (branch && branch.parentElement && branch.parentElement !== document.body) {
      branch = branch.parentElement;
    }
    for (const child of document.body.children) {
      if (child === branch) continue;
      if (SKIP_INERT.has(child.tagName)) continue;
      if (child.matches(PLATFORM_SURFACES)) continue;
      if (on) { child.setAttribute('inert', ''); child.setAttribute('data-melusio-inert', ''); }
      else if (child.hasAttribute('data-melusio-inert')) { child.removeAttribute('inert'); child.removeAttribute('data-melusio-inert'); }
    }
  }

  const FOCUSABLE = 'a[href],area[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  class MelusioOverlay extends HTMLElement {
    connectedCallback() {
      this.scrim = this.querySelector('[data-overlay-scrim]');
      this.panel = this.querySelector('[data-overlay-panel]');
      if (!this.panel) return;
      this.setAttribute('hidden', '');
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      this.panel.setAttribute('tabindex', '-1');
      if (this.dataset.label) this.panel.setAttribute('aria-label', this.dataset.label);

      this.addEventListener('click', (e) => {
        if (e.target === this.scrim || e.target.closest('[data-overlay-close]')) { e.preventDefault(); this.close(); }
      });
      // prevent scroll-chaining to the page from the scrim (iOS defence)
      this.scrim && this.scrim.addEventListener('touchmove', (e) => e.preventDefault(), { passive: false });
      this._onKey = (e) => {
        if (e.key === 'Escape') { e.preventDefault(); this.close(); }
        else if (e.key === 'Tab') this._trap(e);
      };
    }

    get open() { return this.hasAttribute('open'); }

    show(trigger) {
      if (this.open) return;
      OPEN.forEach((o) => o !== this && o.close());   // single open
      this._trigger = trigger || document.activeElement;
      this.removeAttribute('hidden');
      // force reflow so the entrance transition runs
      void this.offsetWidth;
      this.setAttribute('open', '');
      OPEN.add(this);
      lockScroll();
      setInertBackground(this, true);
      document.addEventListener('keydown', this._onKey, true);
      this._focusFirst();
      if (this._trigger && this._trigger.setAttribute) this._trigger.setAttribute('aria-expanded', 'true');
      this.dispatchEvent(new CustomEvent('melusio-overlay:open', { bubbles: true }));
    }

    close() {
      if (!this.open) return;
      this.removeAttribute('open');
      OPEN.delete(this);
      document.removeEventListener('keydown', this._onKey, true);
      setInertBackground(this, false);
      unlockScroll();
      const hide = () => this.setAttribute('hidden', '');
      if (reduced()) hide();
      else {
        const onEnd = (e) => { if (e.target !== this.panel) return; this.panel.removeEventListener('transitionend', onEnd); if (!this.open) hide(); };
        this.panel.addEventListener('transitionend', onEnd);
        setTimeout(() => { if (!this.open) hide(); }, 400); // fallback if no transition fires
      }
      if (this._trigger) {
        if (this._trigger.setAttribute) this._trigger.setAttribute('aria-expanded', 'false');
        if (this._trigger.focus) this._trigger.focus();
      }
      this.dispatchEvent(new CustomEvent('melusio-overlay:close', { bubbles: true }));
    }

    toggle(trigger) { this.open ? this.close() : this.show(trigger); }

    _focusables() {
      return Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    }
    _focusFirst() {
      const f = this._focusables();
      (f[0] || this.panel).focus({ preventScroll: true });
    }
    _trap(e) {
      const f = this._focusables();
      if (!f.length) { e.preventDefault(); this.panel.focus(); return; }
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  if (!customElements.get('melusio-overlay')) customElements.define('melusio-overlay', MelusioOverlay);

  // ---- global trigger delegation ----
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-overlay-open]');
    if (!trigger) return;
    const el = document.getElementById(trigger.getAttribute('data-overlay-open'));
    if (el && el.tagName === 'MELUSIO-OVERLAY') { e.preventDefault(); el.show(trigger); }
  });

  // expose for Cut 3 (cart/quick-view can call window.MelusioOverlay.open('id'))
  window.MelusioOverlay = {
    open: (id, trigger) => { const el = document.getElementById(id); el && el.show && el.show(trigger); },
    close: (id) => { const el = document.getElementById(id); el && el.close && el.close(); },
  };
})();
